import { CommonModule } from "@angular/common";
import { Component, HostListener, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { JobMetaResponse } from "@interfaces";
import { GtagService } from "src/app/containers/gtag/gtag.service";
import { DialogContainerComponent } from "src/app/dialogs/dialog-container/dialog-container.component";

@Component({
    selector: "app-soho-search-box",
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule, DialogContainerComponent],
    templateUrl: "./soho-search-box.component.html",
})
export class SohoSearchBoxComponent implements OnInit {
    public searchForm: FormGroup;
    public searchMeta: JobMetaResponse;
    public showRegions = false;
    public showCategory = false;
    public selectedCategory = "";
    public regions = {
        "United Kingdom": [
            "London",
            "Oxfordshire",
            "Brighton",
            "Somerset",
            "Manchester",
            "Nuneaton",
            "Windsor",
        ],
        "North America": [
            "New York",
            "Los Angeles",
            "Miami",
            "Toronto",
            "Austin",
            "Nashville",
            "Portland",
            "Chicago",
            "Charleston",
            "Palm Springs",
            "Canouan Islands",
            "Atlanta",
            "Houston",
        ],
        "Latin America": [
            "Mexico City",
            "São Paulo",
        ],
        "Europe": [
            "Amsterdam",
            "Berlin",
            "Mykonos",
            "Barcelona",
            "Istanbul",
            "Paris",
            "Rome",
            "Tel Aviv",
            "Stockholm",
            "Copenhagen",
        ],
        "Asia": [
            "Mumbai",
            "Hong Kong",
            "Bangkok",
        ]
    };
    public departments = [{
        name: "Food & Beverage",
        id: 4022778101,
        children: [
            {
                name: "Front of House",
                id: 4022783101,
            },
            {
                name: "Bar",
                id: 4022784101,
            },
            {
                name: "Kitchen",
                id: 4022785101,
            },
            {
                name: "F&B Management",
                id: 4022786101,
            },
        ],
    }, {
        name: "Hotel",
        id: 4022779101,
        children: [
            {
                name: "Front of House",
                id: 4022787101,
            },
            {
                name: "House Keeping",
                id: 4022788101,
            },
            {
                name: "Maintenance",
                id: 4022789101,
            },
            {
                name: "Management",
                id: 4022790101,
            },
        ],
    }, {
        name: "Support",
        id: 4005857101,
        children: [
            {
                name: "Cities Without Houses",
                id: 4005879101,
            },
            {
                name: "Communications",
                id: 4005872101,
            },
            {
                name: "Content & Partnerships",
                id: 4005874101,
            },
            {
                name: "Design",
                id: 4005871101,
            },
            {
                name: "Development",
                id: 4005878101,
            },
            {
                name: "Digital & IT",
                id: 4005869101,
            },
            {
                name: "Finance",
                id: 4005868101,
            },
            {
                name: "Legal",
                id: 4005881101,
            },
            {
                name: "Marketing",
                id: 4005873101,
            },
            {
                name: "Membership & Events",
                id: 4005921101,
            },
            {
                name: "People (HR)",
                id: 4005870101,
            },
            {
                name: "Procurement",
                id: 4005875101,
            },
            {
                name: "Soho Customer Support",
                id: 4005877101,
            },
        ]
    }, {
        name: "Soho Home",
        id: 4022780101,
        children: [
            {
                name: "Sales",
                id: 4022792101,
            },
            {
                name: "Interior Design",
                id: 4022791101,
            },
            {
                name: "Head Office",
                id: 4005876101,
            },
            {
                name: "Management",
                id: 4022793101,
            },
        ]
    }, {
        name: "Soho Works",
        id: 4022781101,
        children: [
            {
                name: "Front of House",
                id: 4022794101,
            },
            {
                name: "Back of House",
                id: 4022795101,
            },
            {
                name: "Events",
                id: 4022796101,
            },
            {
                name: "Management",
                id: 4022797101,
            },
        ]
    }, {
        name: "Cowshed & Spa",
        id: 4022782101,
        children: [
            {
                name: "Front of House",
                id: 4022798101,
            },
            {
                name: "Spa",
                id: 4022799101,
            },
            {
                name: "Management",
                id: 4022800101,
            },
        ]
    }]

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private gtag: GtagService,
        private router: Router,
    ) {
        this.searchForm = this.fb.group({
            keywords: [],
            hashtags: [[]],
            reference: [],
            locationLabel: [],
            categoryId: [],
        });
    }

    ngOnInit(): void {
        this.setupForm();
    }

    private setupForm() {
        if (this.route.snapshot.queryParams?.categoryId) {
            const categoryId = this.route.snapshot.queryParams.categoryId;
            // Find the matching id in this.departments or its children and set selectedCategory
            this.selectedCategory = this.departments.reduce((acc, curr) => {
                if (acc) {
                    return acc;
                }
                const match = curr.id === parseInt(categoryId) ? curr : curr.children.find(child => child.id === parseInt(categoryId));
                return match ? match.name : "";
            }
            , "");
            this.searchForm.get("categoryId").setValue(categoryId);
        }

        if (this.route.snapshot.queryParams?.locationLabel) {
            this.searchForm.get("locationLabel").setValue(this.route.snapshot.queryParams.locationLabel);
        }
    }

    public setLocation(locationLabel: string) {
        this.searchForm.patchValue({ locationLabel });
        this.showRegions = false;
    }

    public setCategory(category: { name: string, id: number }) {
        this.searchForm.patchValue({ categoryId: category.id });
        this.selectedCategory = category.name;
        this.showCategory = false;
    }

    @HostListener("document:keydown.enter", ["$event"])
    public onEnter(event: KeyboardEvent) {
        this.emitSearch();
    }

    public emitSearch() {
        this.gtag.fireSearchEvent(this.searchForm?.value?.keywords);
        this.search();
    }

    public search() {
        let redirectTo = ['/jobs'];
        const payload = this.searchForm.getRawValue();

        if(payload.locationLabel) {
            payload.nestedLocationSearch = true;
        }

        this.router.navigate(redirectTo, { relativeTo: this.route, queryParams: payload });
    }

    public clearSearch() {
        this.searchForm.reset();
        this.selectedCategory = "";
    }

    public returnZero() {
        return 0;
    }
}
